import { UseMutationResult } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { UseFormReturn, useWatch } from "react-hook-form";

import { useTranslation } from "../../core/translation.core";
import useShop from "../../hooks/useShop.hook";
import { MenuItemFormData } from "../../types/menuItem";
import SingleCategorySelectorComp from "../category/singleCategorySelector.comp";
import { LocalInputDefaultLocale } from "../locale/locale.comp";
import GenericFormEditor from "../form/genericFormEditor";
import { EditorSection, Title } from "../reuseables/editor.comp";
import { LabelAndInput, LabelAndSelect } from "../reuseables/form.comp";

interface Props {
  methods: UseFormReturn<MenuItemFormData, unknown>;
  mutation: UseMutationResult<void, Error, MenuItemFormData, unknown>;
  btnText: string;
  cancelPath?: string;
}

export default function MenuItemEditor({ methods, mutation, btnText, cancelPath = "/menuItem" }: Props) {
  const { t, msg } = useTranslation();
  const { isReady } = useRouter();
  const { selectedShop } = useShop();
  const itemType = useWatch({ control: methods.control, name: "type" });

  return (
    <GenericFormEditor methods={methods} mutation={mutation} btnText={btnText} cancelPath={cancelPath}>
      <div id="menu-item-editor" className="space-y-4">
        <EditorSection>
          <Title>{t(msg.product.generalInfo)}</Title>
          <div className="space-y-4 dark:text-slate-200">
            <LabelAndInput<MenuItemFormData>
              register={methods.register}
              fieldName="order"
              label={t(msg.menuItem.order)}
              type="number"
              registerOptions={{ valueAsNumber: true, min: 0 }}
              id="menu-item-order"
            />
            <LabelAndSelect
              methods={methods}
              fieldName="type"
              label={t(msg.menuItem.type)}
              setDefault={false}
              topLabelCss="block"
              options={[
                { label: t(msg.menuItem.typeCategory), value: "category" },
                { label: t(msg.menuItem.typeLink), value: "link" },
              ]}
            />

            {itemType === "category" && isReady && selectedShop && (
              <label className="block">
                <span className="block mb-1">{t(msg.category.category)}</span>
                <SingleCategorySelectorComp methods={methods} name="categoryId" />
              </label>
            )}

            {itemType === "link" && (
              <>
                <LocalInputDefaultLocale
                  register={methods.register}
                  fieldName="label"
                  label={t(msg.menuItem.linkLabel)}
                  required
                />
                <LocalInputDefaultLocale register={methods.register} fieldName="href" label={t(msg.menuItem.linkHref)} required />
              </>
            )}
          </div>
        </EditorSection>
      </div>
    </GenericFormEditor>
  );
}
